import AuthService from "@/services/AuthService";
import { fetchPermissions } from "@/store/Admin";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const roleOptions = [
  { value: "admin", label: "Admin (Full Control)" },
  { value: "moderator", label: "Moderator" },
  { value: "user", label: "User" },
  { value: "publisher", label: "Publisher" },
];

const InviteUserModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch<any>();
  const { permissionList, loading } = useSelector((state: any) => state.admin);

  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [email, setEmail] = useState<string>("");
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchPermissions());
    }
  }, [isOpen, dispatch]);

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm)
        ? prev.filter((p) => p !== perm)
        : [...prev, perm]
    );
  };

  const toggleRole = (role: string) => {
    setRoles((prev) => {
      // Agar admin select hua → sirf admin allow hoga
      if (role === "admin") {
        if (prev.includes("admin")) {
          setPermissions([]); // reset permissions if admin deselected
          return []; // unselect admin
        } else {
          setPermissions([]); // clear permissions when admin selected
          return ["admin"]; // only admin
        }
      }

      // Agar already admin hai → aur koi role select na hone do
      if (prev.includes("admin")) {
        return prev;
      }

      // Normal toggle for other roles
      return prev.includes(role)
        ? prev.filter((r) => r !== role)
        : [...prev, role];
    });
  };

  const handleSubmitInvite = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    if (!roles || roles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }

    await AuthService.invite(email, roles, permissions);

    // reset
    setEmail("");
    setRoles([]);
    setPermissions([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div className="bg-white rounded-2xl w-[500px] p-6">
        <h2 className="text-xl font-semibold text-center mb-6">Send Invite</h2>

        {/* Email */}
        <div className="bg-gray-50 p-4 rounded-xl mb-4">
          <h3 className="text-sm font-medium mb-3">Email</h3>
          <input
            placeholder="Enter email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        {/* Assign Roles */}
        <div className="bg-gray-50 p-4 rounded-xl mb-4">
          <h3 className="text-sm font-medium mb-3">Assign Role(s)</h3>
          {roleOptions.map((r) => {
            const isDisabled = roles.includes("admin") && r.value !== "admin"; // disable all except admin if admin selected
            return (
              <label
                key={r.value}
                className={`flex items-center space-x-2 mb-2 cursor-pointer ${
                  isDisabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <input
                  type="checkbox"
                  value={r.value}
                  checked={roles.includes(r.value)}
                  disabled={isDisabled}
                  onChange={() => toggleRole(r.value)}
                />
                <span>{r.label}</span>
              </label>
            );
          })}
        </div>

        {/* Assign Permissions - only show if Moderator is selected and Admin is NOT */}
        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            roles.includes("moderator") && !roles.includes("admin")
              ? "max-h-96 opacity-100 mt-4"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="bg-gray-50 p-4 rounded-xl">
            <h3 className="text-sm font-medium mb-3">Assign Permissions</h3>
            {loading ? (
              <p className="text-sm text-gray-500">Loading permissions...</p>
            ) : (
              <div className="flex flex-col space-y-2">
                {permissionList?.data?.map((perm: any) => (
                  <label key={perm.key} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={permissions.includes(perm.id)}
                      onChange={() => togglePermission(perm.id)}
                    />
                    <span>
                      {perm.key.charAt(0).toUpperCase() + perm.key.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-between mt-6">
          <button
            className="px-6 py-2 rounded-full border border-black"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-6 py-2 rounded-full bg-[#FF0050] text-white"
            onClick={handleSubmitInvite}
          >
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
